
import { Camera } from "./camera";
import { createBuffer, createVertexArray, describeAttributes } from "./gl/buffer";
import { GlError } from "./gl/context";
import { createProgram, uniformLocations, type Uniforms } from "./gl/shader";
import { loadImage } from "./gl/texture";
import {
  INSTANCE_FLOATS,
  MAX_PARTICLES,
  ParticleField,
  type EffectDef,
  type EffectManifest,
  type Random,
  type SpawnOptions,
} from "./particles";
import { ArrowFlights, arrowAt, arrowTail, type Point } from "./arrowflight";
import { drawArrowFallback } from "./arrowfallback";
import {
  drawLevelUpFallback,
  levelUpVisual,
  LEVEL_UP_FALLBACK_LIFETIME_MS,
  type LevelUpScreenPoint,
} from "./levelupfallback";
import { canUseEntityGlLayer } from "./entitylayer";
import {
  AnchoredGlows,
  DEFAULT_REFINE_GLOW_FAMILY,
  effectKeyOf,
  type AnchoredEffect,
  type RefineGlowFamily,
} from "./effectsanchor";
import { writeFlights, type Flight } from "./projectiles";
import { MM_PER_LEGACY_UNIT } from "../sim/fixed";
import {
  PlacedEffectWindow,
  cullingSphere,
  effectBasis,
  sphereOutsideFrustum,
  windowCapacity,
  type PlacedEffect,
} from "./placedeffects";
import {
  EffectMeshField,
  EffectMeshLayer,
  type MeshDraw,
  type MeshManifest,
} from "./effectsmesh";
import { STONE_DEAD_EVENTS, type MotionEffectEvent } from "./stonespawn";

/** The effect events a body's death plays: a metin stone's FRONT_DEAD motion,
 *  or this build's one puff, at once, for any other body -- including one the
 *  renderer never saw, whose honest answer is the ordinary death. */
export function deathEffectsFor(kind: number | undefined): readonly MotionEffectEvent[] {
  return kind === 3 ? STONE_DEAD_EVENTS : DEATH_PUFF;
}

const DEATH_PUFF: readonly MotionEffectEvent[] = [{ effect: "death", atS: 0, bone: null }];

export function dropItemEffectFor(kind: number | undefined): string | null {
  return kind === 4 ? "drop_item" : null;
}

/** `__OnClickGround` shows `EFFECT_PICK` where a click set a destination the
 *  player then walks to (`PythonPlayerInput.cpp:393-400`,
 *  `PythonPlayerInputMouse.cpp:185-194`). */
export function groundPickEffect(): string {
  return "click_pick";
}

/** `Stun` attaches `EFFECT_STUN` (`InstanceBaseBattle.cpp:683-689`), which
 *  `RegisterCacheEffect` binds to `stun.mse` on "Bip01 Head"
 *  (`playersettingmodule.py:237`, `PythonCharacterManagerModule.cpp:564-580`). */
export function stunEffect(): string {
  return "stun";
}

/** The spark a connected blow raises. A critical sparks the same as any hit:
 *  its own flare is `EFFECT_CRITICAL`, which only `RecvSpecialEffect` attaches,
 *  on the server's `SE_CRITICAL` (`PythonNetworkStreamPhaseGameItem.cpp:795-797`). */
export function hitEffectFor(style: string): string | null {
  return style === "miss" ? null : "hit";
}

export const LEGACY_CAST_MARKER_VNUMS: ReadonlySet<number> = new Set([]);

/** Resolve a cast to a converted native effect, or to the explicitly generic
 * marker for a known but not-yet-converted motion. */
export function skillEffectFor(
  vnum: number,
  skillEffects: Readonly<Record<string, string>>,
  effects: Readonly<Record<string, unknown>>,
): string | null {
  const native = skillEffects[String(vnum)];
  if (native !== undefined) return native;
  return LEGACY_CAST_MARKER_VNUMS.has(vnum) && Object.hasOwn(effects, "hit") ? "hit" : null;
}

export const SPECIAL_EFFECT_NAMES: ReadonlyMap<number, string> = new Map([
  [1, "potion_red"],
  [2, "potion_blue"],
  // `SE_SPEEDUP_GREEN` and `SE_DXUP_PURPLE` (`length.h:638-642`), which
  // `RecvSpecialEffect` turns into `EFFECT_SPEEDUP_GREEN` and
  // `EFFECT_DXUP_PURPLE` (`PythonNetworkStreamPhaseGameItem.cpp:786-794`),
  // registered against `drugup_green.mse` and `drugup_purple.mse` by
  // `playersettingmodule.py:211-212`. No `EffectPacket` in the 2014 server
  // sends either code -- `char_item.cpp` only ever sends `SE_HPUP_RED` and
  // `SE_SPUP_BLUE` for a potion -- so the two flares reach a player through
  // `/effect <n>` (`do_effect`, `cmd_gm.cpp:3435-3444`) and nothing else.
  [3, "potion_green"],
  [4, "potion_purple"],
  // `SE_CRITICAL` (`length.h:643`) -> `EFFECT_CRITICAL`
  // (`PythonNetworkStreamPhaseGameItem.cpp:795-797`), `critical.mse`
  // (`playersettingmodule.py:238`).
  [5, "crit"],
  // `SE_EQUIP_RAMADAN_RING` .. `SE_EQUIP_LOVE_PENDANT` (`length.h:661-664`),
  // registered against these four files by `playersettingmodule.py:219-228`.
  [21, "equip_ramadan_ring"],
  [22, "equip_halloween_candy"],
  [23, "equip_happiness_ring"],
  [24, "equip_love_pendant"],
]);

/** `bPlayPotionSound` (`PythonNetworkStreamPhaseGameItem.cpp:779-794`): the
 *  four drug flares, and only for the main character (`:866-875`). */
export function playsPotionSound(effect: number): boolean {
  return effect >= 1 && effect <= 4;
}

/** `ShowPointEffect` (`PythonCharacterManager.cpp:234-248`): `POINT_LEVEL`
 *  (1, `char.h:100`) attaches `EFFECT_LEVELUP` (`InstanceBaseEffect.cpp:218-221`),
 *  `level_up.mse` (`playersettingmodule.py:345`); `POINT_LEVEL_STEP` (25,
 *  `char.h:125`) attaches `EFFECT_SKILLUP` (`InstanceBaseEffect.cpp:223-226`),
 *  `skillup_1.mse` (`playersettingmodule.py:346`). */
export const POINT_EFFECT_NAMES: ReadonlyMap<number, string> = new Map([
  [1, "level_up"],
  [25, "skill_up"],
]);

const BASE_URL = "effects/";
const MANIFEST_URL = `${BASE_URL}effects.json`;
/** Safari may interrupt one manifest/atlas fetch while a tab is backgrounded.
 * One delayed retry keeps a real hit/cast effect from becoming permanently
 * invisible while retaining the existing no-effects fallback on persistence. */
export const EFFECT_ASSET_FETCH_RETRIES = 1;
export const EFFECT_ASSET_RETRY_DELAY_MS = 250;

export async function loadEffectAssetsWithRetry(
  requestManifest: () => Promise<Response>,
  loadAtlas: (url: string) => Promise<ImageBitmap | HTMLImageElement>,
  wait: (ms: number) => Promise<void> = (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
): Promise<{ manifest: EffectManifest; image: ImageBitmap | HTMLImageElement }> {
  let failure: unknown;
  for (let attempt = 0; attempt <= EFFECT_ASSET_FETCH_RETRIES; attempt += 1) {
    try {
      const response = await requestManifest();
      if (!response.ok) throw new Error(`${MANIFEST_URL}: HTTP ${response.status}`);
      const manifest = (await response.json()) as EffectManifest;
      const image = await loadAtlas(BASE_URL + manifest.atlas.image);
      return { manifest, image };
    } catch (error) {
      failure = error;
      if (attempt === EFFECT_ASSET_FETCH_RETRIES) break;
      await wait(EFFECT_ASSET_RETRY_DELAY_MS);
    }
  }
  throw failure;
}

/** The mesh element's own two assets: the geometry blob and the atlas its
 *  diffuse maps were packed into. Separate from the fetch above and allowed
 *  to fail on its own, so a pack that names neither draws its effects as
 *  particle groups alone rather than not at all. */
export async function loadEffectMeshAssets(
  manifest: EffectManifest & MeshManifest,
  fetchBinary: (url: string) => Promise<Response>,
  loadAtlas: (url: string) => Promise<ImageBitmap | HTMLImageElement>,
): Promise<{ binary: ArrayBuffer; image: ImageBitmap | HTMLImageElement } | null> {
  const layout = manifest.meshBinary;
  const atlas = manifest.meshAtlas;
  if (layout === undefined || atlas === undefined) return null;
  const response = await fetchBinary(BASE_URL + layout.file);
  if (!response.ok) throw new Error(`${layout.file}: HTTP ${response.status}`);
  const binary = await response.arrayBuffer();
  const image = await loadAtlas(BASE_URL + atlas.image);
  return { binary, image };
}

/** Bytes per instance record. The attribute table below carves this up, and
 *  `ParticleField.fill` writes the same fields in the same order; the two are
 *  checked against each other at construction rather than trusted. */
export const INSTANCE_BYTES = INSTANCE_FLOATS * 4;

const OFFSET_CENTRE = 0;
const OFFSET_SIZE = 12;
const OFFSET_ROTATION = 20;
const OFFSET_COLOUR = 24;
const OFFSET_CELL = 40;
const OFFSET_MODE = 56;
const OFFSET_VELOCITY = 60;
/** Where the last attribute ends. If this is not the stride, one of the two
 *  tables has drifted from the other and every particle after the first would
 *  read a neighbour's fields -- which draws *something*, at the wrong size in
 *  the wrong colour, and is exactly the class of bug that survives a look. */
const OFFSET_END = OFFSET_VELOCITY + 12;

/** Four corners of a unit quad, as `(u, v)` in -1..1. Drawn as a triangle strip
 *  so there are four vertices rather than six. */
const QUAD = new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]);

// ---------------------------------------------------------------- arrow tails

export const MODE_SEGMENT = 4;

/** A `CFlyTrace` quad (`FlyTrace.cpp:119-290`): a segment like `MODE_SEGMENT`,
 *  but untextured -- the pass selects the vertex diffuse alone
 *  (`FlyTrace.cpp:151-161`) -- and with a width at each end, carried in the two
 *  half-size floats, head first. */
export const MODE_TRAIL = 5;

export const ARROW_HALF_WIDTH_MM = 50;

export const ARROW_COLOUR = [0xe1 / 255, 0xe1 / 255, 0xe1 / 255, 0.67] as const;

export const ARROW_TEXTURE = "upline";

/** The four names `refineGlowFamily` will accept out of the manifest, so a
 *  stale or hand-edited table cannot make the renderer ask for a key that
 *  cannot exist. The set itself is `effectsanchor.ts`'s `RefineGlowFamily`. */
const REFINE_GLOW_FAMILIES: ReadonlySet<string> = new Set<RefineGlowFamily>([
  "sword",
  "bow",
  "fanbell",
  "smallsword",
]);

export function effectAtlasCell(
  manifest: Pick<EffectManifest, "atlas" | "textures">,
  effect: EffectDef,
): Float32Array | null {
  const frame = effect.emitters[0]?.frames[0];
  if (frame === undefined) return null;
  const rect = manifest.textures[frame];
  if (rect === undefined) return null;
  return new Float32Array([
    rect[0]! / manifest.atlas.width,
    rect[1]! / manifest.atlas.height,
    rect[2]! / manifest.atlas.width,
    rect[3]! / manifest.atlas.height,
  ]);
}

export function writeArrow(
  out: Float32Array,
  at: number,
  head: Point,
  tail: Point,
  cell: ArrayLike<number>,
): boolean {
  // Half the segment: the shader mirrors it about the centre, so this is what
  // reaches the head from the middle.
  const halfX = (head.xMm - tail.xMm) / 2;
  const halfY = (head.yMm - tail.yMm) / 2;
  const halfZ = (head.zMm - tail.zMm) / 2;
  if (halfX === 0 && halfY === 0 && halfZ === 0) return false;

  const i = at * INSTANCE_FLOATS;
  out[i + OFFSET_CENTRE / 4] = tail.xMm + halfX;
  out[i + OFFSET_CENTRE / 4 + 1] = tail.yMm + halfY;
  out[i + OFFSET_CENTRE / 4 + 2] = tail.zMm + halfZ;
  out[i + OFFSET_SIZE / 4] = ARROW_HALF_WIDTH_MM;
  // One, because the length is carried by the half-extent vector below and
  // this is what multiplies it.
  out[i + OFFSET_SIZE / 4 + 1] = 1;
  out[i + OFFSET_ROTATION / 4] = 0;
  out[i + OFFSET_COLOUR / 4] = ARROW_COLOUR[0];
  out[i + OFFSET_COLOUR / 4 + 1] = ARROW_COLOUR[1];
  out[i + OFFSET_COLOUR / 4 + 2] = ARROW_COLOUR[2];
  out[i + OFFSET_COLOUR / 4 + 3] = ARROW_COLOUR[3];
  out[i + OFFSET_CELL / 4] = cell[0];
  out[i + OFFSET_CELL / 4 + 1] = cell[1];
  out[i + OFFSET_CELL / 4 + 2] = cell[2];
  out[i + OFFSET_CELL / 4 + 3] = cell[3];
  out[i + OFFSET_MODE / 4] = MODE_SEGMENT;
  out[i + OFFSET_VELOCITY / 4] = halfX;
  out[i + OFFSET_VELOCITY / 4 + 1] = halfY;
  out[i + OFFSET_VELOCITY / 4 + 2] = halfZ;
  return true;
}

/** Write one tail quad between two samples, `headHalfWidthMm` wide at `head`
 *  and `tailHalfWidthMm` at `tail`, in `colour`; false for two samples at one
 *  point. */
/** `pc/assassin/effect/arrow_01.msf`'s `AttachData` tail: `TailColor
 *  1726079457d` (0x66E1E1E1) as unit RGBA, and `TailSize 1` as the half width
 *  at the head; `TailLength 0.18` is `ARROW_TAIL_S`. */
const ARROW_TAIL_COLOUR = [0xe1 / 255, 0xe1 / 255, 0xe1 / 255, 0x66 / 255] as const;
const ARROW_TAIL_HALF_WIDTH_MM = 1 * MM_PER_LEGACY_UNIT;

/** One bow arrow's `CFlyTrace` (`FlyTrace.cpp:61-69,193-204`): an arrow flies
 *  one straight line at one speed, so the pairwise quads `Render` draws through
 *  its samples are one quad from the head, at the full `TailSize`, to the
 *  sample `TailLength` old, tapered to nothing. */
export function writeArrowTail(out: Float32Array, at: number, head: Point, tail: Point): boolean {
  return writeTrail(out, at, head, tail, ARROW_TAIL_HALF_WIDTH_MM, 0, ARROW_TAIL_COLOUR);
}

export function writeTrail(
  out: Float32Array,
  at: number,
  head: Point,
  tail: Point,
  headHalfWidthMm: number,
  tailHalfWidthMm: number,
  colour: readonly number[],
): boolean {
  const halfX = (head.xMm - tail.xMm) / 2;
  const halfY = (head.yMm - tail.yMm) / 2;
  const halfZ = (head.zMm - tail.zMm) / 2;
  if (halfX === 0 && halfY === 0 && halfZ === 0) return false;
  const i = at * INSTANCE_FLOATS;
  out[i + OFFSET_CENTRE / 4] = tail.xMm + halfX;
  out[i + OFFSET_CENTRE / 4 + 1] = tail.yMm + halfY;
  out[i + OFFSET_CENTRE / 4 + 2] = tail.zMm + halfZ;
  out[i + OFFSET_SIZE / 4] = headHalfWidthMm;
  out[i + OFFSET_SIZE / 4 + 1] = tailHalfWidthMm;
  out[i + OFFSET_ROTATION / 4] = 0;
  out[i + OFFSET_COLOUR / 4] = colour[0];
  out[i + OFFSET_COLOUR / 4 + 1] = colour[1];
  out[i + OFFSET_COLOUR / 4 + 2] = colour[2];
  out[i + OFFSET_COLOUR / 4 + 3] = colour[3];
  out[i + OFFSET_CELL / 4] = 0;
  out[i + OFFSET_CELL / 4 + 1] = 0;
  out[i + OFFSET_CELL / 4 + 2] = 0;
  out[i + OFFSET_CELL / 4 + 3] = 0;
  out[i + OFFSET_MODE / 4] = MODE_TRAIL;
  out[i + OFFSET_VELOCITY / 4] = halfX;
  out[i + OFFSET_VELOCITY / 4 + 1] = halfY;
  out[i + OFFSET_VELOCITY / 4 + 2] = halfZ;
  return true;
}

export function fillFlights(
  out: Float32Array,
  first: number,
  flights: readonly Flight[],
  cellOf: (effectKey: string) => ArrayLike<number> | null,
  nowMs?: number,
): number {
  if (flights.length === 0) return 0;
  const batches = new Map<string, Flight[]>();
  for (const flight of flights) {
    const key = effectKeyOf(flight.kind.script.attachEffect);
    const batch = batches.get(key);
    if (batch === undefined) batches.set(key, [flight]);
    else batch.push(flight);
  }
  let written = 0;
  for (const [key, batch] of batches) {
    const cell = cellOf(key);
    if (cell === null) continue;
    written += writeFlights(out, first + written, batch, cell, nowMs);
  }
  return written;
}

export function writeBillboard(
  out: Float32Array,
  at: number,
  cx: number,
  cy: number,
  cz: number,
  halfW: number,
  halfH: number,
  rotationRad: number,
  r: number,
  g: number,
  b: number,
  a: number,
  cell: ArrayLike<number>,
  mode: number,
): void {
  const i = at * INSTANCE_FLOATS;
  out[i + OFFSET_CENTRE / 4] = cx;
  out[i + OFFSET_CENTRE / 4 + 1] = cy;
  out[i + OFFSET_CENTRE / 4 + 2] = cz;
  out[i + OFFSET_SIZE / 4] = halfW;
  out[i + OFFSET_SIZE / 4 + 1] = halfH;
  out[i + OFFSET_ROTATION / 4] = rotationRad;
  out[i + OFFSET_COLOUR / 4] = r;
  out[i + OFFSET_COLOUR / 4 + 1] = g;
  out[i + OFFSET_COLOUR / 4 + 2] = b;
  out[i + OFFSET_COLOUR / 4 + 3] = a;
  out[i + OFFSET_CELL / 4] = cell[0];
  out[i + OFFSET_CELL / 4 + 1] = cell[1];
  out[i + OFFSET_CELL / 4 + 2] = cell[2];
  out[i + OFFSET_CELL / 4 + 3] = cell[3];
  out[i + OFFSET_MODE / 4] = mode;
  out[i + OFFSET_VELOCITY / 4] = 0;
  out[i + OFFSET_VELOCITY / 4 + 1] = 0;
  out[i + OFFSET_VELOCITY / 4 + 2] = 0;
}

export interface ArrowImpact {
  readonly effect: string;
  readonly options: SpawnOptions;
}

const VERTEX_SOURCE = `#version 300 es
precision highp float;

in vec2 a_corner;
in vec3 a_centreMm;
in vec2 a_halfSizeMm;
in float a_rotation;
in vec4 a_colour;
in vec4 a_cell;
in float a_mode;
in vec3 a_velocityMm;

uniform mat4 u_viewProj;
uniform vec3 u_cameraRight;
uniform vec3 u_cameraUp;
uniform vec3 u_cameraForward;

out vec2 v_uv;
out vec4 v_colour;
flat out float v_textured;

void main() {
  // The quad's own two axes in world space. Four modes, from
  // \`billboardModeOf\` in particles.ts -- see there for which of the original's
  // billboard types collapse into which.
  vec3 right;
  vec3 up;
  if (a_mode < 0.5) {
    right = u_cameraRight;
    up = u_cameraUp;
  } else if (a_mode < 1.5) {
    // Upright: turns to face the camera about the world's vertical only, so a
    // column of flame stays vertical however the camera pitches.
    right = u_cameraRight;
    up = vec3(0.0, 0.0, 1.0);
  } else if (a_mode < 2.5) {
    // Lying flat on the ground, the original's BILLBOARD_TYPE_LIE.
    right = vec3(1.0, 0.0, 0.0);
    up = vec3(0.0, 1.0, 0.0);
  } else if (a_mode < 3.5) {
    // Stretched along the direction of travel. The original scales the streak
    // by \`(1 + log(1 + length)) / length\` (ParticleInstance.cpp:243) so that a
    // fast particle is longer but not proportionally so; that factor is applied
    // here to the *up* axis, which is the axis it scales there.
    float speed = length(a_velocityMm);
    vec3 along = speed > 0.0001 ? a_velocityMm / speed : vec3(0.0, 0.0, 1.0);
    up = along * (speed > 0.0001 ? (1.0 + log(1.0 + speed)) / speed : 1.0);
    right = normalize(cross(up, u_cameraForward));
  } else {
    // A segment between two points the caller already knows: the vector *is*
    // the half-extent, scaled by nothing. See \`MODE_SEGMENT\` on why an arrow
    // cannot use the mode above. The cross product is guarded because an arrow
    // flying straight at or away from the camera is parallel to the view and
    // has no side -- rare, brief, and a NaN-shaped hole in the screen if it is
    // not caught.
    up = a_velocityMm;
    vec3 side = cross(up, u_cameraForward);
    float sideLength = length(side);
    right = sideLength > 0.0001 ? side / sideLength : u_cameraRight;
  }

  float c = cos(a_rotation);
  float s = sin(a_rotation);
  vec2 corner = vec2(a_corner.x * c - a_corner.y * s, a_corner.x * s + a_corner.y * c);

  // A tail quad is as wide as its head at the head end and as its tail at the
  // tail end (FlyTrace.cpp:196-212); every other mode is one size throughout.
  bool trail = a_mode > 4.5;
  float halfWidth = trail ? mix(a_halfSizeMm.y, a_halfSizeMm.x, corner.y * 0.5 + 0.5) : a_halfSizeMm.x;
  float halfLength = trail ? 1.0 : a_halfSizeMm.y;
  vec3 world = a_centreMm
    + right * (corner.x * halfWidth)
    + up * (corner.y * halfLength);
  v_textured = trail ? 0.0 : 1.0;

  gl_Position = u_viewProj * vec4(world, 1.0);
  // The atlas cell, with the quad's -1..1 corner mapped to 0..1 across it.
  v_uv = a_cell.xy + (a_corner * 0.5 + 0.5) * a_cell.zw;
  v_colour = a_colour;
}
`;

const FRAGMENT_SOURCE = `#version 300 es
precision highp float;

in vec2 v_uv;
in vec4 v_colour;
flat in float v_textured;

uniform sampler2D u_atlas;

out vec4 fragColour;

void main() {
  // A tail selects the vertex diffuse alone (FlyTrace.cpp:151-161).
  vec4 texel = v_textured > 0.5 ? texture(u_atlas, v_uv) : vec4(1.0);
  // D3DTOP_MODULATE with the particle's own colour, which is what every one of
  // these emitters asks for (ColorOperationType 4 in the .mse, the default
  // \`ParticleProperty.cpp:42\` sets). The blend factor the pass is set to
  // decides what happens with the result; see \`draw\`.
  fragColour = texel * v_colour;
}
`;

const UNIFORMS = ["u_viewProj", "u_cameraRight", "u_cameraUp", "u_cameraForward", "u_atlas"] as const;

export class EffectRenderer {
  private readonly sharedContext: boolean;
  private canvas: HTMLCanvasElement | null = null;
  private gl: WebGL2RenderingContext | null = null;
  private program: WebGLProgram | null = null;
  private uniforms: Uniforms<(typeof UNIFORMS)[number]> | null = null;
  private vao: WebGLVertexArrayObject | null = null;
  private instanceBuffer: WebGLBuffer | null = null;
  private atlas: WebGLTexture | null = null;
  private field: ParticleField | null = null;
  private manifest: EffectManifest | null = null;
  private readonly instances = new Float32Array(MAX_PARTICLES * INSTANCE_FLOATS);
  private lastMs = 0;
  /** How many instances the previous frame drew, so an empty frame that
   *  follows a full one still clears the canvas. See `draw`. */
  private lastDrawn = 0;
  /** Set once, so a failed load says so once rather than once a frame. */
  private complained = false;
  /** The arrows in the air. Simulated here for the same reason the particles
   *  are: this is the layer that draws them, `arrowflight.ts` is the arithmetic
   *  with no GL in it, and one `clear` has to be able to drop both. */
  private readonly arrows = new ArrowFlights();
  /** What each arrow sets off when it arrives, by flight key. Emptied by the
   *  landing that spends it, so it is bounded by what is in the air. */
  private readonly impacts = new Map<string, ArrowImpact>();
  /** `ARROW_TEXTURE`'s rectangle in the atlas, in 0..1, resolved once when the
   *  manifest lands. Null until then, and on a pack that does not carry it --
   *  in which case arrows fly and land but nothing is drawn for them, which is
   *  this layer's usual bargain. */
  private arrowCell: Float32Array | null = null;
  /** Level-up events remain visible while the opaque Canvas2D fallback has no
   * particle field. Capped like every event table in the client. */
  private fallbackLevelUps: Array<{ options: SpawnOptions; bornMs: number }> = [];
  /** The bone-anchored glows to keep alive this frame -- refine glow, buff/
   *  affect loops. Replaced wholesale each frame by `setAnchoredEffects`, so a
   *  glow stops being drawn the moment the caller stops listing it. */
  private anchored: AnchoredGlows | null = null;
  private activeAnchored: readonly AnchoredEffect[] = [];
  /** The skill projectiles in the air this frame, as `FrameFx.frame` returned
   *  them. Replaced wholesale each frame like `activeAnchored`, and for the
   *  same reason: the flights are simulated in `projectiles.ts` against the
   *  frame's live positions, and this layer only draws what it is handed. */
  private activeFlights: readonly Flight[] = [];
  /** Effect key -> its first emitter's first atlas cell, in 0..1, resolved
   *  once when the manifest lands. What a flight's own `.mse` draws with. */
  private readonly effectCells = new Map<string, Float32Array>();
  /** The mesh elements: the animated textured bodies an effect can carry
   *  beside its particle groups (`EffectLib/EffectMesh.cpp`). Null until the
   *  manifest lands, and on a pack that converted none. */
  private meshes: EffectMeshField | null = null;
  private meshLayer: EffectMeshLayer | null = null;
  /** Whether the mesh half has finished trying: true once its two assets are
   *  in, and also once they have failed or the pack named none. A harness
   *  that photographs a frame waits for this and not only for `ready`, which
   *  the particles reach first. */
  private meshesSettled = false;
  /** What the mesh field said to draw this frame, kept so the fallback path
   *  and the debug hook can read it without stepping the field again. */
  private meshDraws: readonly MeshDraw[] = [];
  /** The map's own effects (`CArea::__SetObjectInstance_SetEffect`,
   *  `Area.cpp:531-570`): their placements, and once the manifest is in, the
   *  window that keeps them alive and the two fields they simulate in, sized
   *  for the map's busiest window so no placed effect evicts another. */
  private placedEffects: readonly PlacedEffect[] = [];
  private placedMapSize: { readonly patchesX: number; readonly patchesY: number } | null = null;
  private placed: {
    readonly window: PlacedEffectWindow;
    readonly particles: ParticleField;
    readonly meshes: EffectMeshField;
    readonly bases: readonly Float64Array[];
    readonly shown: Uint8Array;
    readonly instances: Float32Array;
  } | null = null;
  private placedLastMs = 0;

  /** Where the particle field's jitter comes from. `Math.random` in a browser;
   *  a seeded generator wherever two runs have to spawn the same particles. */
  private readonly random: Random;

  constructor(
    private readonly hostCanvas: HTMLCanvasElement,
    sharedGl: WebGL2RenderingContext | null = null,
    sharedCanvas: HTMLCanvasElement | null = null,
    random: Random = Math.random,
  ) {
    this.random = random;
    this.sharedContext = sharedGl !== null && sharedCanvas !== null;
    if (!canUseEntityGlLayer(hostCanvas, this.sharedContext)) return;
    if (hostCanvas.parentNode === null) return;

    const canvas = this.sharedContext ? sharedCanvas! : document.createElement("canvas");
    if (!this.sharedContext) {
      stack(canvas);
      // Named in the DOM so a harness can read this standalone layer rather
      // than guessing which of the page's transparent canvases is the effect
      // one. Shared mode deliberately leaves the terrain canvas named as such.
      canvas.dataset.layer = "effects";
    }
    this.canvas = canvas;

    try {
      if (OFFSET_END !== INSTANCE_BYTES) {
        // Inside the try, so a layout that has drifted costs the effect layer
        // and says why in the console, rather than throwing out of the entity
        // painter's constructor and dropping the whole client to Canvas2D.
        throw new GlError(
          `the particle instance layout ends at ${OFFSET_END} bytes but the stride is ${INSTANCE_BYTES}`,
        );
      }
      const gl = resolveEffectContext(this.sharedContext ? sharedGl : null, canvas);
      if (gl === null) throw new GlError("webgl2 context unavailable for the effect layer");
      this.gl = gl;
      this.program = createProgram(gl, VERTEX_SOURCE, FRAGMENT_SOURCE, "particles");
      this.uniforms = uniformLocations(gl, this.program, UNIFORMS);

      this.vao = createVertexArray(gl, "particles");
      gl.bindVertexArray(this.vao);

      const quad = createBuffer(gl, "particle quad");
      gl.bindBuffer(gl.ARRAY_BUFFER, quad);
      gl.bufferData(gl.ARRAY_BUFFER, QUAD, gl.STATIC_DRAW);
      describeAttributes(gl, this.program, 8, [
        { name: "a_corner", components: 2, type: gl.FLOAT, normalized: false, offset: 0, divisor: 0 },
      ]);

      this.instanceBuffer = createBuffer(gl, "particle instances");
      gl.bindBuffer(gl.ARRAY_BUFFER, this.instanceBuffer);
      gl.bufferData(gl.ARRAY_BUFFER, this.instances.byteLength, gl.DYNAMIC_DRAW);
      this.pointAtInstance(0);
      gl.bindVertexArray(null);
    } catch (error) {
      console.warn(`effects unavailable (${String(error)}); combat will draw without them`);
      // Never tear down the terrain when shared setup fails. The standalone
      // context is disposable; the shared one belongs to the whole scene.
      if (!this.sharedContext) this.gl?.getExtension("WEBGL_lose_context")?.loseContext();
      this.gl = null;
      this.canvas = null;
      return;
    }

    void this.load();
  }

  /** True once the manifest and atlas are in and something can actually be
   *  drawn. Read by the debug hook and by the e2e harness. */
  get ready(): boolean {
    return this.field !== null && this.atlas !== null;
  }

  get particleCount(): number {
    return this.field?.particleCount ?? 0;
  }

  /** Which effects were converted. For a harness that wants to play all of
   *  them without hard-coding the list. */
  get names(): readonly string[] {
    return this.manifest === null ? [] : Object.keys(this.manifest.effects);
  }

  /** Whether this pack converted that effect. Asked before an attach, so an
   *  asset a given pack does not carry is a silent nothing rather than a
   *  bookkeeping entry for an effect that never spawned. */
  has(key: string): boolean {
    return this.manifest !== null && Object.hasOwn(this.manifest.effects, key);
  }

  /** The effect a skill vnum plays, or null. Native mappings come from
   *  `build_effects.py`'s `SKILL_EFFECTS`, which now covers every playable
   *  vnum; `LEGACY_CAST_MARKER_VNUMS` is the fallback path for a future one
   *  that does not. */
  effectForSkill(vnum: number): string | null {
    if (this.manifest === null) return null;
    return skillEffectFor(vnum, this.manifest.skillEffects, this.manifest.effects);
  }

  effectForSpecial(effect: number): string | null {
    return this.converted(SPECIAL_EFFECT_NAMES.get(effect));
  }

  effectForPoint(point: number): string | null {
    return this.converted(POINT_EFFECT_NAMES.get(point));
  }

  private converted(key: string | undefined): string | null {
    if (key === undefined || this.manifest === null) return null;
    return key in this.manifest.effects ? key : null;
  }

  setAnchoredEffects(effects: readonly AnchoredEffect[]): void {
    this.activeAnchored = effects;
  }

  setFlights(flights: readonly Flight[]): void {
    this.activeFlights = flights;
  }

  refineGlowFamily(vnum: number): RefineGlowFamily {
    const named = this.manifest?.weaponRefineFamilies?.[String(vnum)];
    return named === undefined || !REFINE_GLOW_FAMILIES.has(named)
      ? DEFAULT_REFINE_GLOW_FAMILY
      : (named as RefineGlowFamily);
  }

  /** Start an effect. Silently does nothing before the manifest lands or on a
   *  name this build did not convert. */
  spawn(key: string, options: SpawnOptions): void {
    if (this.field !== null) {
      this.field.spawn(key, options);
      this.meshes?.spawn(key, options);
      return;
    }
    if (key !== "level_up") return;
    this.fallbackLevelUps.push({ options, bornMs: performance.now() });
    if (this.fallbackLevelUps.length > 4) this.fallbackLevelUps.shift();
  }

  /** `DeactiveEffectInstance` (`EffectManager.cpp:244-253`) on the spawn whose
   *  `SpawnOptions.instance` this is. */
  deactivate(instance: number): void {
    this.field?.deactivate(instance);
    this.meshes?.deactivate(instance);
  }

  follow(entityId: number, xMm: number, yMm: number, zMm: number, basis: ArrayLike<number> | null = null): void {
    this.field?.follow(entityId, xMm, yMm, zMm, basis);
    this.meshes?.follow(entityId, xMm, yMm, zMm, basis);
  }

  /** Whether this renderer tracks a body for `followId`. */
  following(followId: number): boolean {
    return this.field?.following(followId) ?? false;
  }

  /** The body is gone: forget it, and drop everything hung on it --
   *  `ClearAttachingEffect` on the destroyed instance. */
  unfollow(entityId: number): void {
    this.field?.unfollow(entityId);
    this.meshes?.unfollow(entityId);
  }

  /** Detach one named effect from one body -- `__SetAffect`'s clearing arm
   *  (`UserInterface/InstanceBaseEffect.cpp:952-957`). */
  stopFollowing(entityId: number, key: string): void {
    this.field?.stopFollowing(entityId, key);
    this.meshes?.stopFollowing(entityId, key);
  }

  /** Whether that effect is already attached to that body, the test
   *  `__SetAffect` makes before attaching a second copy (`:947-951`). False
   *  with no particle field, where nothing is attached to anything. */
  isFollowing(entityId: number, key: string): boolean {
    return (this.field?.isFollowing(entityId, key) ?? false)
      || (this.meshes?.isFollowing(entityId, key) ?? false);
  }

  /** Whether the mesh element's assets have been resolved one way or the
   *  other, so a caller that wants a whole effect on screen knows to wait. */
  get meshesReady(): boolean {
    return this.meshesSettled;
  }

  /** How many mesh elements are alive. `particleCount`'s other half: an
   *  effect whose whole visible body is a mesh has no particles at all. */
  get meshCount(): number {
    return this.meshes?.count ?? 0;
  }

  launchArrow(
    key: string,
    from: Point,
    to: Point,
    nowMs: number,
    impact: ArrowImpact | null,
  ): boolean {
    if (this.arrows.launch(key, from, to, nowMs) === null) return false;
    if (impact !== null) this.impacts.set(key, impact);
    return true;
  }

  get arrowCount(): number {
    return this.arrows.count;
  }

  drawFallbackArrows(ctx: CanvasRenderingContext2D, camera: Camera, nowMs: number): void {
    if (this.gl !== null && this.arrowCell !== null) return;
    for (const flight of this.arrows.inFlight(nowMs)) {
      const headPoint = arrowAt(flight, nowMs);
      const tailPoint = arrowTail(flight, nowMs);
      const head = camera.project(headPoint.xMm, headPoint.yMm, headPoint.zMm);
      const tail = camera.project(tailPoint.xMm, tailPoint.yMm, tailPoint.zMm);
      if (head.behind || tail.behind) continue;
      drawArrowFallback(ctx, { x: tail.x, y: tail.y }, { x: head.x, y: head.y });
    }
  }

  /** A new world: drop everything the old one was still burning, and every
   *  arrow still crossing it. */
  /** The effects a map places, and how many patches it has. Replaces any
   *  earlier map's; nothing is alive until `drawPlaced` first sees the
   *  player. */
  setPlacedEffects(effects: readonly PlacedEffect[], patchesX: number, patchesY: number): void {
    this.placedEffects = effects;
    this.placedMapSize = { patchesX, patchesY };
    this.buildPlaced();
  }

  private buildPlaced(): void {
    const manifest = this.manifest as (EffectManifest & MeshManifest) | null;
    const size = this.placedMapSize;
    const gl = this.gl;
    if (manifest === null || size === null || gl === null || this.placedEffects.length === 0) {
      this.placed = null;
      return;
    }
    const capacity = windowCapacity(this.placedEffects, size.patchesX, size.patchesY, (key) => {
      const effect = manifest.effects[key] as (EffectDef & { meshes?: readonly unknown[] }) | undefined;
      if (effect === undefined) return null;
      return {
        emitters: effect.emitters.length,
        particles: effect.emitters.reduce((sum, emitter) => sum + emitter.maxParticles, 0),
        meshes: effect.meshes?.length ?? 0,
      };
    });
    const particles = Math.max(1, capacity.particles);
    const instances = new Float32Array(particles * INSTANCE_FLOATS);
    if (this.instanceBuffer !== null && instances.byteLength > this.instances.byteLength) {
      gl.bindBuffer(gl.ARRAY_BUFFER, this.instanceBuffer);
      gl.bufferData(gl.ARRAY_BUFFER, instances.byteLength, gl.DYNAMIC_DRAW);
    }
    this.placed = {
      window: new PlacedEffectWindow(this.placedEffects, size.patchesX, size.patchesY),
      particles: new ParticleField(manifest, this.random, particles, Math.max(1, capacity.emitters)),
      meshes: new EffectMeshField(manifest, Math.max(1, capacity.meshes)),
      bases: this.placedEffects.map((effect) => effectBasis(effect.yawDeg, effect.pitchDeg, effect.rollDeg)),
      shown: new Uint8Array(this.placedEffects.length),
      instances,
    };
    this.placedLastMs = 0;
  }

  /** `CMapOutdoor::RenderEffect` (`MapOutdoorRender.cpp:338-349`): bring the
   *  window up to the player's patch, step every live placed effect, and draw
   *  those whose bounding sphere the frustum does not wholly exclude, with the
   *  depth test the world pass keeps and no depth writes
   *  (`EffectInstance.cpp:101`). */
  drawPlaced(camera: Camera, nowMs: number, playerXMm: number, playerYMm: number): void {
    const gl = this.gl;
    const placed = this.placed;
    const program = this.program;
    const uniforms = this.uniforms;
    const atlas = this.atlas;
    const manifest = this.manifest;
    if (gl === null || placed === null || program === null || uniforms === null || atlas === null || manifest === null) {
      return;
    }

    const { entered, left } = placed.window.update(playerXMm, playerYMm);
    for (const index of left) {
      placed.particles.unfollow(index);
      placed.meshes.unfollow(index);
    }
    for (const index of entered) {
      const effect = this.placedEffects[index];
      const options: SpawnOptions = {
        xMm: effect.position.xMm,
        yMm: effect.position.yMm,
        zMm: effect.position.zMm,
        followId: index,
        basis: placed.bases[index],
      };
      placed.particles.spawn(effect.effect, options);
      placed.meshes.spawn(effect.effect, options);
    }

    const dtS = this.placedLastMs === 0 ? 0 : (nowMs - this.placedLastMs) / 1000;
    this.placedLastMs = nowMs;
    placed.particles.step(dtS);
    placed.meshes.step(dtS);

    for (const index of placed.window.live) {
      const effect = this.placedEffects[index];
      const def = manifest.effects[effect.effect];
      const sphere = def === undefined ? null : cullingSphere(effect, def);
      placed.shown[index] = sphere === null ||
        !sphereOutsideFrustum(camera.viewProj, sphere.xMm, sphere.yMm, sphere.zMm, sphere.radiusMm)
        ? 1
        : 0;
    }
    const shown = (followId: number): boolean => placed.shown[followId] === 1;

    const written = placed.particles.fill(placed.instances, shown);
    const meshDraws = placed.meshes.visible(shown);
    if (written.count === 0 && meshDraws.length === 0) return;

    gl.viewport(0, 0, gl.drawingBufferWidth, gl.drawingBufferHeight);
    const right = camera.billboardRight;
    const forward = forwardOf(camera);
    const up: [number, number, number] = [
      right[1] * forward[2] - right[2] * forward[1],
      right[2] * forward[0] - right[0] * forward[2],
      right[0] * forward[1] - right[1] * forward[0],
    ];
    if (written.count > 0) {
      gl.useProgram(program);
      gl.bindVertexArray(this.vao);
      gl.bindBuffer(gl.ARRAY_BUFFER, this.instanceBuffer);
      gl.bufferSubData(gl.ARRAY_BUFFER, 0, placed.instances, 0, written.count * INSTANCE_FLOATS);
      gl.uniformMatrix4fv(uniforms.u_viewProj, false, camera.viewProj);
      gl.uniform3f(uniforms.u_cameraRight, right[0], right[1], right[2]);
      gl.uniform3f(uniforms.u_cameraUp, up[0], up[1], up[2]);
      gl.uniform3f(uniforms.u_cameraForward, forward[0], forward[1], forward[2]);
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, atlas);
      gl.uniform1i(uniforms.u_atlas, 0);
      gl.enable(gl.BLEND);
      gl.depthMask(false);
      gl.enable(gl.DEPTH_TEST);
      const alpha = written.count - written.additive;
      if (alpha > 0) {
        this.pointAtInstance(written.additive);
        gl.blendFuncSeparate(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA, gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
        gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, alpha);
        this.pointAtInstance(0);
      }
      if (written.additive > 0) {
        gl.blendFuncSeparate(gl.SRC_ALPHA, gl.ONE, gl.ZERO, gl.ONE);
        gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, written.additive);
      }
      gl.disable(gl.BLEND);
      gl.depthMask(true);
      gl.bindVertexArray(null);
    }
    this.meshLayer?.draw(meshDraws, camera, { right, up, forward }, true);
  }

  clear(): void {
    this.field?.clear();
    this.meshes?.clear();
    this.meshDraws = [];
    this.arrows.clear();
    this.impacts.clear();
    this.fallbackLevelUps = [];
    this.activeAnchored = [];
    this.activeFlights = [];
    this.buildPlaced();
  }

  /** Paint queued level gains when no drawable particle field exists. */
  drawFallbackLevelUps(ctx: CanvasRenderingContext2D, camera: Camera, nowMs: number): void {
    if (this.field !== null) return;
    const active: Array<{ options: SpawnOptions; bornMs: number }> = [];
    for (const event of this.fallbackLevelUps) {
      const visual = levelUpVisual(event.bornMs, nowMs);
      if (visual.alpha <= 0) continue;
      active.push(event);
      const basePoint = camera.project(event.options.xMm, event.options.yMm, event.options.zMm);
      const topPoint = camera.project(event.options.xMm, event.options.yMm, event.options.zMm + 900);
      if (basePoint.behind || topPoint.behind) continue;
      drawLevelUpFallback(
        ctx,
        { x: basePoint.x, y: basePoint.y } satisfies LevelUpScreenPoint,
        { x: topPoint.x, y: topPoint.y } satisfies LevelUpScreenPoint,
        visual,
      );
    }
    this.fallbackLevelUps = active.filter((event) => nowMs - event.bornMs < LEVEL_UP_FALLBACK_LIFETIME_MS);
  }

  private landArrows(nowMs: number): void {
    for (const flight of this.arrows.collect(nowMs)) {
      const impact = this.impacts.get(flight.key);
      if (impact === undefined) continue;
      this.impacts.delete(flight.key);
      this.field?.spawn(impact.effect, impact.options);
    }
  }

  /** Write every arrow still in the air into the instance buffer, after the
   *  `first` instances the particles took, and say how many were written. */
  private fillArrows(out: Float32Array, first: number, nowMs: number): number {
    let written = 0;
    for (const flight of this.arrows.inFlight(nowMs)) {
      if (first + written >= MAX_PARTICLES) break;
      const head = arrowAt(flight, nowMs);
      const tail = arrowTail(flight, nowMs);
      if (writeArrowTail(out, first + written, head, tail)) written += 1;
    }
    return written;
  }

  draw(camera: Camera, nowMs: number, pixelWidth: number, pixelHeight: number): void {
    // Before every early return below, and deliberately: a client with no GL
    // context still launches arrows -- `launchArrow` is arithmetic and its
    // answer decides what the caller draws instead -- and a table nothing
    // drains is the one leak `ArrowFlights` was written to make impossible.
    this.landArrows(nowMs);

    const gl = this.gl;
    const canvas = this.canvas;
    const field = this.field;
    if (gl === null || canvas === null || field === null) return;

    const dtS = this.lastMs === 0 ? 0 : (nowMs - this.lastMs) / 1000;
    this.lastMs = nowMs;
    field.step(dtS);
    this.meshes?.step(dtS);
    this.meshDraws = this.meshes?.visible() ?? [];

    if (
      field.particleCount === 0 &&
      this.arrowCount === 0 &&
      this.activeAnchored.length === 0 &&
      this.activeFlights.length === 0 &&
      this.meshDraws.length === 0 &&
      this.lastDrawn === 0
    )
      return;

    // Attached on first draw rather than in the constructor, for exactly
    // `creaturemesh.ts`'s reason: the terrain canvas is not yet a sibling to
    // insert relative to while the renderer is still being built, so a canvas
    // attached then lands *under* the opaque terrain and is never seen.
    if (!this.sharedContext && canvas.parentNode === null && this.hostCanvas.parentNode !== null) {
      this.hostCanvas.parentNode.insertBefore(canvas, this.hostCanvas);
    }
    if (!this.sharedContext && (canvas.width !== pixelWidth || canvas.height !== pixelHeight)) {
      canvas.width = pixelWidth;
      canvas.height = pixelHeight;
    }

    gl.viewport(0, 0, gl.drawingBufferWidth, gl.drawingBufferHeight);
    if (!this.sharedContext) {
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
    }

    const atlas = this.atlas;
    const program = this.program;
    const uniforms = this.uniforms;
    if (atlas === null || program === null || uniforms === null) {
      this.lastDrawn = 0;
      return;
    }

    const written = field.fill(this.instances);
    // The arrows go after every particle, in a range of their own: they are
    // neither of the two blend modes `fill` packs from both ends, and a third
    // range costs one more `drawArraysInstanced` on the frames where anything
    // is actually flying.
    const arrowsDrawn = this.fillArrows(this.instances, written.count, nowMs);
    // The skill projectiles come next, in a range of their own for the same
    // reason the arrows have one: they are the same additive stretched
    // billboard and neither of the two blend ranges `fill` packs. One more
    // `drawArraysInstanced` only on the frames something is actually flying.
    const flightsDrawn = fillFlights(
      this.instances,
      written.count + arrowsDrawn,
      this.activeFlights,
      (key) => this.effectCells.get(key) ?? this.arrowCell,
      nowMs,
    );
    // The anchored glows follow the arrows in a range of their own, for the
    // same reason: they are additive but neither of the two blend ranges
    // `fill` packs, and a fourth range costs one more `drawArraysInstanced`
    // only on the frames a glow is actually lit.
    const anchoredDrawn =
      this.anchored === null
        ? 0
        : this.anchored.fill(
            this.instances,
            written.count + arrowsDrawn + flightsDrawn,
            this.activeAnchored,
            nowMs,
            MAX_PARTICLES,
            writeBillboard,
          );
    this.lastDrawn = written.count + arrowsDrawn + flightsDrawn + anchoredDrawn;
    if (this.lastDrawn === 0 && this.meshDraws.length === 0) return;

    if (this.lastDrawn > 0) {
      gl.useProgram(program);
      gl.bindVertexArray(this.vao);
      gl.bindBuffer(gl.ARRAY_BUFFER, this.instanceBuffer);
      gl.bufferSubData(gl.ARRAY_BUFFER, 0, this.instances, 0, this.lastDrawn * INSTANCE_FLOATS);
    }

    if (this.lastDrawn > 0) gl.uniformMatrix4fv(uniforms.u_viewProj, false, camera.viewProj);
    const right = camera.billboardRight;
    const forward = forwardOf(camera);
    // Camera up completes the basis: `up = right x forward` in a right-handed
    // frame. Derived rather than read because `Camera` publishes the horizontal
    // right vector (for the tree billboards) and its eye and target, but not
    // its own up.
    const up: [number, number, number] = [
      right[1] * forward[2] - right[2] * forward[1],
      right[2] * forward[0] - right[0] * forward[2],
      right[0] * forward[1] - right[1] * forward[0],
    ];
    if (this.lastDrawn > 0) {
      gl.uniform3f(uniforms.u_cameraRight, right[0], right[1], right[2]);
      gl.uniform3f(uniforms.u_cameraUp, up[0], up[1], up[2]);
      gl.uniform3f(uniforms.u_cameraForward, forward[0], forward[1], forward[2]);

      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, atlas);
      gl.uniform1i(uniforms.u_atlas, 0);

      gl.enable(gl.BLEND);
      gl.depthMask(false);
      gl.disable(gl.DEPTH_TEST);
    }


    const alpha = written.count - written.additive;
    if (alpha > 0) {
      this.pointAtInstance(written.additive);
      gl.blendFuncSeparate(
        gl.SRC_ALPHA,
        gl.ONE_MINUS_SRC_ALPHA,
        gl.ONE,
        gl.ONE_MINUS_SRC_ALPHA,
      );
      gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, alpha);
      this.pointAtInstance(0);
    }

    if (written.additive > 0) {
      gl.blendFuncSeparate(gl.SRC_ALPHA, gl.ONE, gl.ZERO, gl.ONE);
      gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, written.additive);
    }

    // And the arrows, last and additive -- `D3DBLEND_SRCALPHA` to
    // `D3DBLEND_ONE` is what `CFlyTrace::Render` sets for the ribbon too
    // (`GameLib/FlyTrace.cpp:137-138`), so this is the same pass the original
    // draws a tail in rather than a mode chosen for it here.
    if (arrowsDrawn > 0) {
      this.pointAtInstance(written.count);
      gl.blendFuncSeparate(gl.SRC_ALPHA, gl.ONE, gl.ZERO, gl.ONE);
      gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, arrowsDrawn);
      this.pointAtInstance(0);
    }

    // The skill projectiles, drawn in the same additive pass and for the same
    // reason: `CFlyTrace::Render` sets `D3DBLEND_SRCALPHA` to `D3DBLEND_ONE`
    // for a flying object's trail too (`GameLib/FlyTrace.cpp:137-138`).
    if (flightsDrawn > 0) {
      this.pointAtInstance(written.count + arrowsDrawn);
      gl.blendFuncSeparate(gl.SRC_ALPHA, gl.ONE, gl.ZERO, gl.ONE);
      gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, flightsDrawn);
      this.pointAtInstance(0);
    }

    // And the bone-anchored glows, additive like the arrows -- the refine
    // sparkle and the buff loop both add light to the blade.
    if (anchoredDrawn > 0) {
      this.pointAtInstance(written.count + arrowsDrawn + flightsDrawn);
      gl.blendFuncSeparate(gl.SRC_ALPHA, gl.ONE, gl.ZERO, gl.ONE);
      gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, anchoredDrawn);
      this.pointAtInstance(0);
    }
    gl.disable(gl.BLEND);
    // This pass never writes depth, but the terrain pass on the shared canvas
    // must be able to write it again on the next frame. Leaving the mask false
    // would make that frame's clear and terrain draw preserve stale depths,
    // causing hills and creatures to disappear behind last frame's geometry.
    gl.depthMask(true);
    gl.bindVertexArray(null);

    // And the mesh elements last, in their own program: `CEffectInstance::
    // OnRender` renders every particle instance and then every mesh instance
    // (`EffectLib/EffectInstance.cpp:112-113`), and each sub-mesh carries its
    // own blend factors rather than sharing one of the ranges above.
    this.meshLayer?.draw(this.meshDraws, camera, { right, up, forward });
  }

  private pointAtInstance(first: number): void {
    const gl = this.gl;
    const program = this.program;
    if (gl === null || program === null || this.instanceBuffer === null) return;
    const base = first * INSTANCE_BYTES;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.instanceBuffer);
    describeAttributes(gl, program, INSTANCE_BYTES, [
      { name: "a_centreMm", components: 3, type: gl.FLOAT, normalized: false, offset: base + OFFSET_CENTRE, divisor: 1 },
      { name: "a_halfSizeMm", components: 2, type: gl.FLOAT, normalized: false, offset: base + OFFSET_SIZE, divisor: 1 },
      { name: "a_rotation", components: 1, type: gl.FLOAT, normalized: false, offset: base + OFFSET_ROTATION, divisor: 1 },
      { name: "a_colour", components: 4, type: gl.FLOAT, normalized: false, offset: base + OFFSET_COLOUR, divisor: 1 },
      { name: "a_cell", components: 4, type: gl.FLOAT, normalized: false, offset: base + OFFSET_CELL, divisor: 1 },
      { name: "a_mode", components: 1, type: gl.FLOAT, normalized: false, offset: base + OFFSET_MODE, divisor: 1 },
      { name: "a_velocityMm", components: 3, type: gl.FLOAT, normalized: false, offset: base + OFFSET_VELOCITY, divisor: 1 },
    ]);
  }

  private async load(): Promise<void> {
    if (this.gl === null) {
      this.meshesSettled = true;
      return;
    }
    try {
      const { manifest, image } = await loadEffectAssetsWithRetry(
        () => fetch(MANIFEST_URL),
        (url) => loadImage(url),
      );
      this.install(manifest, image);
      await this.loadMeshes(manifest);
    } catch (error) {
      if (this.complained) return;
      this.complained = true;
      console.warn(`effects unavailable (${String(error)}); combat will draw without them`);
    }
  }

  /** Adopt a manifest and its atlas: the half of `load` that has the two in
   *  hand, so a caller that already holds them does not have to fetch. */
  install(manifest: EffectManifest, image: ImageBitmap | HTMLImageElement): void {
    const gl = this.gl;
    if (gl === null) return;
    const texture = gl.createTexture();
    if (texture === null) throw new GlError("could not create the effect atlas texture");
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image);
    // No mipmaps: the atlas is a shelf pack with one pixel of padding, and a
    // mip level past the first blends neighbouring cells together however
    // wide the gutter is. Particles are drawn large and blurry by nature, so
    // the aliasing a mip chain would fix is not visible on them anyway.
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

    this.atlas = texture;
    this.manifest = manifest;
    this.field = new ParticleField(manifest, this.random);
    this.meshes = new EffectMeshField(manifest);
    this.anchored = new AnchoredGlows(manifest);
    this.buildPlaced();
    this.effectCells.clear();
    for (const [key, effect] of Object.entries(manifest.effects)) {
      const cell = effectAtlasCell(manifest, effect);
      if (cell !== null) this.effectCells.set(key, cell);
    }
    const rect = manifest.textures[ARROW_TEXTURE];
    if (rect === undefined) {
      console.warn(`effects: no '${ARROW_TEXTURE}' in the atlas; arrows will not be drawn`);
    } else {
      this.arrowCell = new Float32Array([
        rect[0] / manifest.atlas.width,
        rect[1] / manifest.atlas.height,
        rect[2] / manifest.atlas.width,
        rect[3] / manifest.atlas.height,
      ]);
    }
    console.log(
      `effects: ${Object.keys(manifest.effects).length} loaded ` +
        `(${manifest.atlas.width}x${manifest.atlas.height} atlas)`,
    );
  }

  /** Adopt the mesh element's geometry and atlas. Separate from `install` so a
   *  pack that carries neither still gets its particles, and so a caller that
   *  already holds the two does not have to fetch them. */
  installMeshes(binary: ArrayBuffer, image: ImageBitmap | HTMLImageElement): void {
    const gl = this.gl;
    if (gl === null || this.manifest === null) return;
    const layer = new EffectMeshLayer(gl);
    layer.install(this.manifest, binary, image);
    this.meshLayer = layer.ready ? layer : null;
  }

  private async loadMeshes(manifest: EffectManifest): Promise<void> {
    try {
      const assets = await loadEffectMeshAssets(manifest, (url) => fetch(url), (url) => loadImage(url));
      if (assets !== null) this.installMeshes(assets.binary, assets.image);
    } catch (error) {
      console.warn(`effect meshes unavailable (${String(error)}); effects will draw as particles alone`);
    } finally {
      this.meshesSettled = true;
    }
  }
}

/** The camera's unit view direction, eye to target. */
function forwardOf(camera: Camera): [number, number, number] {
  const eye = camera.eyeMm;
  const dx = camera.xMm - eye.xMm;
  const dy = camera.yMm - eye.yMm;
  const dz = camera.zMm - eye.zMm;
  const length = Math.hypot(dx, dy, dz);
  if (length === 0) return [0, 1, 0];
  return [dx / length, dy / length, dz / length];
}

/** Reuse the terrain context when Safari refuses another WebGL2 context. */
export function resolveEffectContext(
  sharedGl: WebGL2RenderingContext | null,
  canvas: HTMLCanvasElement,
): WebGL2RenderingContext | null {
  return sharedGl ?? canvas.getContext("webgl2", {
    alpha: true,
    antialias: true,
    depth: false,
    preserveDrawingBuffer: true,
  });
}

function stack(element: HTMLElement): void {
  const style = element.style;
  style.position = "fixed";
  style.left = "0";
  style.top = "0";
  style.width = "100vw";
  style.height = "100vh";
  style.zIndex = "0";
}
